import React, { useMemo, ChangeEvent } from 'react';
import { debounce, startCase } from 'lodash';

import Form from 'react-bootstrap/Form';

import Select from 'components/elements/FormInput/select/Select';
import DatePicker from 'components/elements/FormInput/date/DatePicker';
import BurnCenterFilter from 'components/elements/BurnCenterFilter';

import { DataUploadShortLabels, DataUploadOptions } from 'utils/enums';
import MaskedInput from 'react-text-mask';

type FilterKeys = 'search' | 'types' | 'from' | 'to' | 'burnCenters' | 'fileTypes';

const MaskedDateInput = React.forwardRef<
  MaskedInput,
  React.ComponentProps<typeof MaskedInput>
>((inputProps, ref) => (
  <MaskedInput
    {...inputProps}
    ref={ref}
    mask={[
      // Month: 01-12
      /[0-1]/,
      /\d/,
      '/',
      // Day: 01-31
      /[0-3]/,
      /\d/,
      '/',
      // Year
      /\d/,
      /\d/,
      /\d/,
      /\d/,
    ]}
  />
));
export interface UploadListFiltersProps {
  filters: {
    types: string[];
    from?: Date | null;
    to?: Date | null;
    burnCenters?: number[];
    fileTypes?: string[];
  };
  onFilterChange: (key: FilterKeys, value: any) => void;
  burnCenterId?: number;
}

const UploadListFilters: React.FC<UploadListFiltersProps> = ({
  filters,
  onFilterChange,
  burnCenterId,
}) => {
  const debouncedOnFilterChange = useMemo(
    () => debounce(onFilterChange, 500),
    [onFilterChange],
  );

  return (
    <div
      className="mb-3"
      style={{
        display: 'flex',
        gap: '1rem',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ width: '100%', maxWidth: '25rem', minWidth: 0 }}>
        <Form.Control
          onChange={(e: ChangeEvent<HTMLInputElement>) => debouncedOnFilterChange('search', e.target.value)}
          placeholder="Search..."
        />
      </div>
      {!burnCenterId && (
        <div style={{ width: '100%', maxWidth: '25rem', minWidth: 0 }}>
          <BurnCenterFilter
            isMulti
            value={filters.burnCenters}
            onChange={(value: string | number | (string | number)[] | null) => onFilterChange('burnCenters', value)}
          />
        </div>
      )}
      <div style={{ width: '100%', maxWidth: '25rem', minWidth: 0 }}>
        <Select
          value={filters.types}
          onChange={(value) => onFilterChange('types', value)}
          placeholder="Type of Data"
          options={DataUploadOptions}
          isMulti
          formatOptionLabel={(option, { context }) => (context === 'menu' ? option.label : DataUploadShortLabels[option.value])}
        />
      </div>
      <div style={{ width: '100%', maxWidth: '15rem', minWidth: 0 }}>
        <Select
          value={filters.fileTypes}
          onChange={(value) => onFilterChange('fileTypes', value)}
          placeholder="File Type"
          options={[
            { label: 'CSV', value: 'csv' },
            { label: 'XML', value: 'xml' },
            { label: 'XFR', value: 'xfr' },
          ]}
          isClearable
        />
      </div>
      {(['from', 'to'] as const).map((key) => (
        <div key={key} style={{ width: '100%', maxWidth: '9rem', minWidth: 0 }}>
          <DatePicker
            selected={filters[key]}
            onChange={(value: Date | null) => onFilterChange(key, value)}
            placeholderText={startCase(key)}
            dateFormat="MM/dd/yyyy"
            isClearable
            fullWidth
            customInput={(
              <MaskedDateInput
                mask={[
                  /[0-1]/, /\d/, '/', /[0-3]/, /\d/, '/', /\d/, /\d/, /\d/, /\d/,
                ]}
              />
      )}
          />
        </div>
      ))}
    </div>
  );
};

export default UploadListFilters;
